// src/components/legal/TermsOfSale.jsx
//
// /conditions-generales - the terms of sale and use for professionals
// (establishment owners) subscribing to QRProof. Its content follows what the
// code actually does: plans in pricing_plan, the free month
// (backend/utils/freeTrial.ts), statistics per plan (utils/statsAccess.ts),
// Paddle checkout, cancellation at the end of the billing period, the rating
// rules (routes/validate.ts) and the kiosks (lent with a subscription and
// taken back by the ambassador on cancellation). [À COMPLÉTER] marks what only
// the company can fill in. A draft: to be reviewed by a lawyer before use.
import React from 'react';
import { Box, Container, Typography, Table, TableBody, TableCell, TableHead, TableRow, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const TODO = (label) => <Box component="span" sx={{ bgcolor: '#FFF3CD', px: 0.5 }}>[À COMPLÉTER : {label}]</Box>;

const PLANS = [
  ['Standard', '9,99 €', '1', 'Non'],
  ['Plus', '19,99 €', '3', 'Oui'],
  ['Premium', '29,99 €', '10', 'Oui'],
];

export default function TermsOfSale() {
  const h2 = { mt: 4, mb: 1, fontWeight: 800 };
  const p = { mb: 1.5 };
  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 900, mb: 1 }}>Conditions générales de vente et d'utilisation</Typography>
      <Typography color="text.secondary">QRProof · professionnels · mise à jour le {TODO('date de publication')}</Typography>

      <Typography variant="h5" component="h2" sx={h2}>1. Objet</Typography>
      <Typography sx={p}>
        Les présentes conditions régissent l'abonnement au service QRProof et la mise à disposition des bornes, entre{' '}
        {TODO('raison sociale')} (« QRProof », voir les <Link component={RouterLink} to="/mentions-legales">mentions légales</Link>)
        et le professionnel qui inscrit un ou plusieurs établissements (le « Client »). Le service est réservé aux
        professionnels ; il n'est pas destiné aux consommateurs.
      </Typography>
      <Typography sx={p}>
        Le Client accepte ces conditions en créant son compte ou en souscrivant une formule. La version applicable est celle
        en ligne au jour de la souscription.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>2. Le service</Typography>
      <Typography sx={p}>
        QRProof permet à un établissement de recueillir des notes vérifiées : le client de l'établissement scanne un QR code
        affiché sur place, avec l'appareil photo de son téléphone ou l'application YummiTracker. Une note n'est validée que si
        le téléphone se trouve à moins de 20 mètres de l'établissement, et un même appareil ne peut noter un établissement
        qu'une fois par jour (une fois par séjour pour un hébergement). Le service comprend aussi, selon la formule : le badge
        à intégrer sur le site du Client, les alertes et le rapport hebdomadaire par e-mail, les statistiques, les événements
        dans l'application YummiTracker et le mode conciergerie pour les hébergements.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>3. Formules et prix</Typography>
      <Table size="small" sx={{ mb: 1.5 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Formule</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Prix par mois</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Établissements</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Statistiques</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {PLANS.map(([name, price, facilities, stats]) => (
            <TableRow key={name}>
              <TableCell>{name}</TableCell>
              <TableCell>{price}</TableCell>
              <TableCell>{facilities}</TableCell>
              <TableCell>{stats}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography sx={p}>
        Les prix sont exprimés {TODO('hors taxes ou toutes taxes comprises')}. QRProof peut faire évoluer ses prix ; un
        changement s'applique à la période de facturation suivante, après information du Client par e-mail au moins{' '}
        {TODO('délai de préavis, par exemple 30 jours')} à l'avance.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>4. Mois gratuit</Typography>
      <Typography sx={p}>
        Un nouveau compte peut utiliser QRProof gratuitement pendant 30 jours à compter de sa création, pour un seul
        établissement. À la fin de ce mois, le QR code ne permet plus de noter tant qu'aucune formule n'est souscrite. Les
        notes déjà reçues sont conservées.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>5. Paiement</Typography>
      <Typography sx={p}>
        Le paiement est encaissé par Paddle (Paddle.com Market Ltd), qui agit comme revendeur du service et émet les factures.
        L'abonnement est payé d'avance pour chaque période et se renouvelle automatiquement. En cas d'échec de paiement, l'accès
        aux fonctionnalités payantes peut être suspendu jusqu'à régularisation.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>6. Durée, changement de formule et résiliation</Typography>
      <Typography sx={p}>
        L'abonnement mensuel est sans engagement. Le Client peut changer de formule ou résilier à tout moment depuis son
        tableau de bord (« Modifier votre offre »). La résiliation arrête le renouvellement : l'accès reste ouvert jusqu'à la
        fin de la période déjà payée, sans remboursement de cette période. Ensuite, le QR code ne permet plus de noter et le
        badge s'affiche comme inactif ; ils redeviennent actifs dès qu'une nouvelle formule est souscrite.
      </Typography>
      <Typography sx={p}>
        Un abonnement annuel, lorsqu'il est proposé, engage le Client pour 12 mois.{' '}
        {TODO('conditions de l\'abonnement annuel : prix, remise, résiliation anticipée')}
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>7. Bornes</Typography>
      <Typography sx={p}>
        QRProof propose deux bornes à poser dans l'établissement : la <b>borne simple</b>, qui affiche le QR code de
        notation, et la <b>borne tips</b>, qui y ajoute une tirelire pour les pourboires en espèces. La borne fonctionne avec
        un abonnement en cours.
      </Typography>
      <Typography sx={p}>
        <b>Borne mise à disposition avec un abonnement.</b> Lorsqu'une borne est fournie sans supplément avec un abonnement,
        elle est <b>prêtée</b> : elle reste la propriété de QRProof pendant toute la durée de l'abonnement. Le Client en a la
        garde, l'utilise dans l'établissement prévu et s'engage à ne pas la céder, la démonter ni la modifier.
      </Typography>
      <Typography sx={p}>
        <b>Restitution.</b> À la fin de l'abonnement, quelle qu'en soit la cause, la borne prêtée est <b>récupérée dans
        l'établissement par l'ambassadeur QRProof</b>, à une date convenue avec le Client. Le Client la rend complète, avec son
        alimentation, et en bon état d'usage. Une borne non rendue, perdue ou détériorée au-delà de l'usure normale pourra
        être facturée {TODO('montant de la borne non rendue, par type')}.
      </Typography>
      <Typography sx={p}>
        <b>Borne achetée.</b> Une borne vendue au Client lui appartient dès son paiement complet ; elle n'est pas récupérée à
        la fin de l'abonnement.
      </Typography>
      <Typography sx={p}>
        <b>Pourboires.</b> Les pourboires déposés dans la borne tips appartiennent au Client et à son personnel. QRProof ne
        les collecte pas, ne les compte pas et n'y a jamais accès. Le Client en assure seul la garde et la répartition.
      </Typography>
      <Typography sx={p}>
        <b>Garantie.</b> Une borne qui ne fonctionne plus du fait d'un défaut est réparée ou remplacée par QRProof
        {TODO('durée de garantie et conditions de remplacement')}.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>8. Obligations du Client</Typography>
      <Typography component="ul" sx={{ pl: 3, mb: 1.5 }}>
        <li>Afficher le QR code uniquement dans l'établissement concerné et ne pas le diffuser ailleurs (site, réseaux sociaux, e-mails).</li>
        <li>Ne pas noter son propre établissement, ni faire noter des personnes qui n'y sont pas venues.</li>
        <li>Ne pas conditionner une remise, un cadeau ou un service à une bonne note.</li>
        <li>Renseigner la position exacte de l'établissement et garder ses identifiants confidentiels.</li>
      </Typography>
      <Typography sx={p}>
        En cas de manquement, QRProof peut retirer les notes concernées et suspendre le compte, après en avoir informé le Client.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>9. Responsabilité</Typography>
      <Typography sx={p}>
        QRProof fait ses meilleurs efforts pour que le service soit disponible, sans garantir une disponibilité continue.
        QRProof n'est pas responsable du contenu des notes laissées par les clients de l'établissement. Sa responsabilité
        est limitée {TODO('plafond de responsabilité, par exemple les sommes payées sur les 12 derniers mois')}.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>10. Données personnelles</Typography>
      <Typography sx={p}>
        Le traitement des données est décrit dans la <Link component={RouterLink} to="/confidentialite">politique de confidentialité</Link>.
      </Typography>

      <Typography variant="h5" component="h2" sx={h2}>11. Droit applicable et litiges</Typography>
      <Typography sx={p}>
        Les présentes conditions sont soumises au droit français. À défaut d'accord amiable, tout litige relève du tribunal
        de commerce de {TODO('ville')}.
      </Typography>
    </Container>
  );
}
